import { formatSecondsAsTimecode } from '@kiao-voice/core'
import { DOWNLOAD_FORMAT_ROWS, type AudioBatchDownloadFormat } from './download-formats'
import { AUDIO_BATCH_ALLOWED_EXTENSIONS } from './types'
import type { TranscriptSegmentView, TranscriptSpeaker } from './types'

/**
 * Strip a trailing recognized audio/video extension so the transcript title
 * renders clean (`發音課-CBR` not `發音課-CBR.mp3`). New uploads already store an
 * extension-free name; this also tidies LEGACY rows uploaded before that change.
 * Only an allowlisted extension is removed, so a dotted title like
 * `Lesson 1.5 demo` keeps its interior dot.
 */
export function stripAudioExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  if (dot <= 0) return name
  const ext = name.slice(dot + 1).toLowerCase()
  return (AUDIO_BATCH_ALLOWED_EXTENSIONS as readonly string[]).includes(ext)
    ? name.slice(0, dot)
    : name
}

/**
 * Build the filename used when downloading a transcript in the chosen
 * format. Strips a trailing audio/video extension (allowlist-aware, so a
 * clean title with an interior dot like `Lesson 1.5 demo` is preserved) and
 * tacks on the format's extension (`.vtt` / `.srt` / `.txt`). Falls back to a
 * stable `audio-{itemId}.<ext>` when the item has no recorded filename.
 */
export function deriveTranscriptFilename(
  filename: string | null,
  itemId: number,
  format: AudioBatchDownloadFormat
): string {
  const ext = DOWNLOAD_FORMAT_ROWS[format].extension
  if (!filename) return `audio-${itemId}.${ext}`
  return `${stripAudioExtension(filename)}.${ext}`
}

/**
 * Per-speaker tone, KIAO black-white system (replaces the old rainbow palette).
 * Identity is carried by the always-shown "Speaker N" label (or the user's
 * name) plus a pure-color swatch — no A/B/C letter. The tone is an ink→gray
 * ladder used for the swatch fill and the cue's left bar. `fill` is the swatch
 * background; `ink` is the readable text on it (used by the waveform track's
 * code label, the one place a number sits on the fill). Assigned by
 * first-appearance order so a speaker keeps the same tone across the roster,
 * cue badges, and the waveform speaker track. Cycles past the ladder.
 */
export interface SpeakerTone {
  fill: string
  ink: string
}

export const SPEAKER_TONES: readonly SpeakerTone[] = [
  { fill: '#161616', ink: '#ffffff' },
  { fill: '#595954', ink: '#ffffff' },
  { fill: '#9a9a95', ink: '#161616' },
  { fill: '#c2c1bc', ink: '#161616' },
  { fill: '#7d7d78', ink: '#ffffff' },
] as const

export interface RosterEntry {
  code: string
  /** User-set name, or null to render the localized default `Speaker {code}`. */
  displayName: string | null
  tone: SpeakerTone
}

/**
 * Build the ordered, toned speaker roster the viewer renders.
 *
 * Order = first appearance across `segments` (so tones follow reading order),
 * then any roster speakers that have no segments yet (e.g. a just-added
 * speaker), then — defensively — any segment code missing from the roster.
 * `displayName` is taken from the roster (null when absent or unnamed).
 */
export function buildSpeakerRoster(
  speakers: TranscriptSpeaker[],
  segments: TranscriptSegmentView[]
): RosterEntry[] {
  const nameByCode = new Map(speakers.map((s) => [s.code, s.displayName]))
  const order: string[] = []
  const seen = new Set<string>()

  const push = (code: string | null | undefined) => {
    if (!code || seen.has(code)) return
    seen.add(code)
    order.push(code)
  }

  for (const segment of segments) push(segment.speaker)
  for (const speaker of speakers) push(speaker.code)

  return order.map((code, i) => ({
    code,
    displayName: nameByCode.get(code) ?? null,
    tone: SPEAKER_TONES[i % SPEAKER_TONES.length],
  }))
}

/**
 * Best character offset to split a segment at: the sentence punctuation
 * (，。！？、,.!?；; — split AFTER it) nearest the text's midpoint, falling back
 * to the raw midpoint when there's no interior punctuation. Always returns a
 * point strictly inside the text (0 < point < length) for non-trivial input.
 * Drives the ✂ toolbar button; Enter-in-text uses the caret offset instead.
 */
export function splitPoint(text: string): number {
  const s = text ?? ''
  const mid = Math.floor(s.length / 2)
  let best = -1
  let bestDist = Infinity
  const re = /[，。！？、,.!?；;]/g
  let m: RegExpExecArray | null
  while ((m = re.exec(s)) !== null) {
    const p = m.index + 1 // split AFTER the punctuation
    if (p > 0 && p < s.length) {
      const d = Math.abs(p - mid)
      if (d < bestDist) {
        bestDist = d
        best = p
      }
    }
  }
  return best > 0 ? best : mid
}

/**
 * In single-cue playback, the time to stop at for a given playhead `time`:
 * the end of the cue containing `time`, or — when `time` sits in a gap between
 * cues — the end of the next upcoming cue. Returns null when there is no cue at
 * or after `time` (nothing left to play). Used so the main Play button, the
 * mode toggle, and seeking all stop at a cue boundary consistently, matching
 * the per-cue ▶ behavior.
 */
export function singleCueStopTime(
  segments: TranscriptSegmentView[],
  time: number
): number | null {
  const cur =
    segments.find((s) => time >= s.start && time < s.end) ??
    segments.find((s) => s.start >= time)
  return cur ? cur.end : null
}

/** Plain-text transcript (one segment per line, index order) for copy / TXT export. */
export function joinSegmentsText(segments: TranscriptSegmentView[]): string {
  return segments
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((s) => s.text)
    .join('\n')
}

/** Display an ISO datetime as `YYYY/MM/DD` (the prototype's list/detail date style). */
export function formatDate(iso: string): string {
  return iso.slice(0, 10).replace(/-/g, '/')
}

/**
 * Display an ISO datetime as `M/D` (compact due-date for the expiry banner).
 * String-based like `formatDate` so it's timezone-agnostic and deterministic.
 * Known limitation (LOW): this reads the UTC calendar day, so near UTC midnight
 * the shown M/D can be one day off from the viewer's local day — consistent with
 * `formatDate` (same convention). The day-COUNT shown next to it (daysUntilExpiry)
 * is always correct. A per-user-timezone fix should update `formatDate` too.
 */
export function formatMonthDay(iso: string): string {
  const month = iso.slice(5, 7)
  const day = iso.slice(8, 10)
  if (!/^\d{2}$/.test(month) || !/^\d{2}$/.test(day)) return ''
  return `${Number(month)}/${Number(day)}`
}

/**
 * Retention thresholds, mirrored from the backend (`RETENTION_WARN_DAYS`).
 * A completed item within `WARN` days of deletion shows a countdown; within
 * `SOON` days the countdown switches to the error tone (imminent, unrecoverable).
 */
export const RETENTION_WARN_DAYS = 7
export const RETENTION_SOON_DAYS = 3

/**
 * Whole days remaining until `expiresAt` (audio + transcript permanent deletion),
 * or null when there is no clock yet (`expiresAt` null = not completed). The
 * current partial day counts as a full day remaining (ceil), so ~20h left reads
 * as "1 day" and only a passed deadline goes ≤ 0 ("today"). Matches the backend
 * window of `completedAt + 30 days`.
 */
export function daysUntilExpiry(expiresAt: string | null, now: Date = new Date()): number | null {
  if (!expiresAt) return null
  const ms = new Date(expiresAt).getTime() - now.getTime()
  if (!Number.isFinite(ms)) return null
  const days = Math.ceil(ms / 86_400_000)
  // Math.ceil of a fraction in (-1, 0) yields -0; normalize to +0.
  return days === 0 ? 0 : days
}

/**
 * Compact playback timecode: `M:SS` under an hour, `H:MM:SS` beyond.
 *
 * Unpadded minutes on purpose — this sits inside the player's transport row,
 * where `5:05` is what a media control reads like. The shared formatter pads by
 * default for the surfaces that render a column of stamps.
 */
export function formatTimecode(secs: number): string {
  return formatSecondsAsTimecode(secs, { padMinutes: false })
}

/**
 * Precise edit timecode `HH:MM:SS.mmm` (segment in/out points).
 *
 * The millisecond digits are NOT decoration and must not be rounded away: a
 * transcript's cue boundaries are what you align audio and cuts against, and
 * people do type exact values in here. A shorter display would buy a few
 * characters of row width and cost the precision the field exists for.
 *
 * Quantize to milliseconds FIRST, then split, so a value just under a whole
 * second (59.9999) can't print as `…:59.1000` or `:60.000`.
 *
 * The display resolution is load-bearing for the inline editor — `commitTime`
 * ignores parse deltas under half a millisecond, because a value carrying a
 * finer fraction round-trips through this format and can only agree with the
 * underlying number to 0.001s.
 */
export function formatTimecodeMs(secs: number): string {
  const safe = Number.isFinite(secs) && secs > 0 ? secs : 0
  const ms = Math.round(safe * 1000)
  const h = Math.floor(ms / 3600000)
  const m = Math.floor((ms % 3600000) / 60000)
  const s = Math.floor((ms % 60000) / 1000)
  const f = ms % 1000
  const p = (n: number, w = 2) => n.toString().padStart(w, '0')
  return `${p(h)}:${p(m)}:${p(s)}.${p(f, 3)}`
}

/**
 * Parse a `HH:MM:SS.mmm` / `MM:SS` / `SS(.mmm)` timecode back to seconds (for the
 * inline timecode editor). Returns null on any malformed/negative input so the
 * caller can revert to the previous value.
 */
export function parseTimecode(input: string): number | null {
  const trimmed = input.trim()
  if (!trimmed) return null
  const parts = trimmed.split(':')
  let secs = 0
  for (const part of parts) {
    const n = Number.parseFloat(part)
    if (part.trim() === '' || Number.isNaN(n)) return null
    secs = secs * 60 + n
  }
  return Number.isFinite(secs) && secs >= 0 ? secs : null
}
